import React from 'react';
import {
  UserIcon,
  ClockIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

// Unified status configuration
export type StatusType = 'lead' | 'campaign' | 'call' | 'webhook' | 'result';

export interface StatusConfig {
  label: string;
  color: string;
  icon?: React.ComponentType<{ className?: string }>;
  dotColor?: string;
}

// Centralized status configurations
const STATUS_CONFIGS: Record<StatusType, Record<string, StatusConfig>> = {
  lead: {
    new: {
      label: 'New',
      color: 'badge-new',
      icon: UserIcon,
      dotColor: 'bg-primary',
    },
    queued: {
      label: 'Queued',
      color: 'badge-queued',
      icon: ClockIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    calling: {
      label: 'Calling',
      color: 'badge-calling',
      icon: PhoneIcon,
      dotColor: 'bg-warning',
    },
    completed: {
      label: 'Completed',
      color: 'badge-completed',
      icon: CheckCircleIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    called: {
      label: 'Called',
      color: 'badge-secondary',
      icon: PhoneIcon,
      dotColor: 'bg-gray-400 dark:bg-gray-500',
    },
    interested: {
      label: 'Interested',
      color: 'badge-interested',
      icon: CheckCircleIcon,
      dotColor: 'bg-success',
    },
    not_interested: {
      label: 'Not Interested',
      color: 'badge-not_interested',
      icon: XCircleIcon,
      dotColor: 'bg-destructive',
    },
    do_not_call: {
      label: 'Do Not Call',
      color: 'badge-do_not_call',
      icon: ExclamationTriangleIcon,
      dotColor: 'bg-accent',
    },
    failed: {
      label: 'Failed',
      color: 'badge-failed',
      icon: XCircleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
    no_answer: {
      label: 'No Answer',
      color: 'badge-warning',
      icon: PhoneIcon,
      dotColor: 'bg-orange-400 dark:bg-orange-500',
    },
    invalid: {
      label: 'Invalid',
      color: 'badge-invalid',
      icon: ExclamationTriangleIcon,
      dotColor: 'bg-muted-foreground',
    },
    busy: {
      label: 'Voicemail',
      color: 'badge-secondary',
      icon: PhoneIcon,
      dotColor: 'bg-purple-400 dark:bg-purple-500',
    },
    gatekeeper: {
      label: 'Gatekeeper',
      color: 'badge-warning',
      icon: ExclamationTriangleIcon,
      dotColor: 'bg-amber-400 dark:bg-amber-500',
    },
    operator: {
      label: 'Operator',
      color: 'badge-info',
      icon: PhoneIcon,
      dotColor: 'bg-cyan-400 dark:bg-cyan-500',
    },
    app_out: {
      label: 'App Out',
      color: 'badge-info',
      icon: PhoneIcon,
      dotColor: 'bg-indigo-400 dark:bg-indigo-500',
    },
    spanish: {
      label: 'Spanish',
      color: 'badge-warning',
      icon: ExclamationTriangleIcon,
      dotColor: 'bg-rose-400 dark:bg-rose-500',
    },
    app_in: {
      label: 'App In',
      color: 'badge-info',
      icon: PhoneIcon,
      dotColor: 'bg-sky-400 dark:bg-sky-500',
    },
    submitted: {
      label: 'Submitted',
      color: 'badge-info',
      icon: CheckCircleIcon,
      dotColor: 'bg-violet-400 dark:bg-violet-500',
    },
    contract: {
      label: 'Contract',
      color: 'badge-warning',
      icon: ClockIcon,
      dotColor: 'bg-amber-400 dark:bg-amber-500',
    },
    funded: {
      label: 'Funded',
      color: 'badge-interested',
      icon: CheckCircleIcon,
      dotColor: 'bg-emerald-400 dark:bg-emerald-500',
    },
    follow_up: {
      label: 'Follow Up',
      color: 'badge-info',
      icon: ClockIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    hangup: {
      label: 'Hang Up',
      color: 'badge-warning',
      icon: PhoneIcon,
      dotColor: 'bg-yellow-400 dark:bg-yellow-500',
    },
  },
  campaign: {
    draft: {
      label: 'Draft',
      color: 'badge-draft',
      icon: UserIcon,
      dotColor: 'bg-gray-400 dark:bg-gray-500',
    },
    active: {
      label: 'Active',
      color: 'badge-active',
      icon: PlayIcon,
      dotColor: 'bg-emerald-400 dark:bg-emerald-500',
    },
    paused: {
      label: 'Paused',
      color: 'badge-paused',
      icon: PauseIcon,
      dotColor: 'bg-amber-400 dark:bg-amber-500',
    },
    completed: {
      label: 'Completed',
      color: 'badge-completed',
      icon: CheckCircleIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    cancelled: {
      label: 'Cancelled',
      color: 'badge-cancelled',
      icon: XCircleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
    running: {
      label: 'Running',
      color: 'badge-running',
      icon: ArrowPathIcon,
      dotColor: 'bg-accent',
    },
  },
  call: {
    pending: {
      label: 'Pending',
      color: 'badge-new',
      icon: ClockIcon,
      dotColor: 'bg-gray-400 dark:bg-gray-500',
    },
    queued: {
      label: 'Queued',
      color: 'badge-queued',
      icon: ClockIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    calling: {
      label: 'Calling',
      color: 'badge-calling',
      icon: PhoneIcon,
      dotColor: 'bg-amber-400 dark:bg-amber-500',
    },
    completed: {
      label: 'Completed',
      color: 'badge-completed',
      icon: CheckCircleIcon,
      dotColor: 'bg-emerald-400 dark:bg-emerald-500',
    },
    failed: {
      label: 'Failed',
      color: 'badge-failed',
      icon: XCircleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
    cancelled: {
      label: 'Cancelled',
      color: 'badge-cancelled',
      icon: XCircleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
  },
  webhook: {
    pending: {
      label: 'Pending',
      color: 'badge-warning',
      icon: ClockIcon,
      dotColor: 'bg-warning',
    },
    processing: {
      label: 'Processing',
      color: 'badge-info',
      icon: ArrowPathIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    processed: {
      label: 'Processed',
      color: 'badge-success',
      icon: CheckCircleIcon,
      dotColor: 'bg-emerald-400 dark:bg-emerald-500',
    },
    failed: {
      label: 'Failed',
      color: 'badge-error',
      icon: XCircleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
  },
  result: {
    interested: {
      label: 'Interested',
      color: 'badge-interested',
      icon: CheckCircleIcon,
      dotColor: 'bg-emerald-400 dark:bg-emerald-500',
    },
    not_interested: {
      label: 'Not Interested',
      color: 'badge-not_interested',
      icon: XCircleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
    callback_requested: {
      label: 'Callback Requested',
      color: 'badge-info',
      icon: PhoneIcon,
      dotColor: 'bg-blue-400 dark:bg-blue-500',
    },
    wrong_number: {
      label: 'Wrong Number',
      color: 'badge-warning',
      icon: ExclamationTriangleIcon,
      dotColor: 'bg-warning',
    },
    voicemail: {
      label: 'Voicemail',
      color: 'badge-secondary',
      icon: PhoneIcon,
      dotColor: 'bg-purple-400 dark:bg-purple-500',
    },
    hung_up: {
      label: 'Hung Up',
      color: 'badge-muted',
      icon: XCircleIcon,
      dotColor: 'bg-gray-400 dark:bg-gray-500',
    },
    technical_error: {
      label: 'Technical Error',
      color: 'badge-error',
      icon: ExclamationTriangleIcon,
      dotColor: 'bg-red-400 dark:bg-red-500',
    },
  },
};

export interface StatusBadgeProps {
  status: string;
  type: StatusType;
  variant?: 'default' | 'dot' | 'icon-only';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  type,
  variant = 'default',
  size = 'md',
  className,
}) => {
  const config = STATUS_CONFIGS[type]?.[status];

  if (!config) {
    // Fallback for unknown statuses
    return (
      <span className={clsx('badge badge-outline', className)}>
        {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  }

  const Icon = config.icon;
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-0.5',
    lg: 'text-sm px-3 py-1',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  if (variant === 'dot') {
    return (
      <div className={clsx('flex items-center space-x-2', className)}>
        <div className={clsx('h-2 w-2 rounded-full', config.dotColor)} />
        <span className="text-sm text-foreground">{config.label}</span>
      </div>
    );
  }

  if (variant === 'icon-only' && Icon) {
    return (
      <div className={clsx('flex items-center justify-center', className)}>
        <Icon className={clsx(iconSizes[size], 'text-muted-foreground')} />
      </div>
    );
  }

  return (
    <span className={clsx('badge', config.color, sizeClasses[size], className)}>
      {Icon && (
        <Icon className={clsx(iconSizes[size], '-ml-0.5 mr-1.5')} />
      )}
      {config.label}
    </span>
  );
};

// Utility function to get status configuration
export const getStatusConfig = (status: string, type: StatusType): StatusConfig | null => {
  return STATUS_CONFIGS[type]?.[status] || null;
};

// Utility function to get status dot color
export const getStatusDotColor = (status: string, type: StatusType): string => {
  return STATUS_CONFIGS[type]?.[status]?.dotColor || 'bg-muted-foreground';
};

export default StatusBadge;
